import { type CalendarItem } from '../types'
import {
  CalendarMenuActionIds,
  type CalendarMenuActionId,
  type CalendarMenuCapabilities,
  type CalendarMenuOption,
} from './types'

const LABELS: Readonly<Record<CalendarMenuActionId, string>> = {
  [CalendarMenuActionIds.addToBoard]: 'Add to Board',
  [CalendarMenuActionIds.addToPlan]: 'Add to Plan',
  [CalendarMenuActionIds.copyLink]: 'Copy Link',
  [CalendarMenuActionIds.delete]: 'Delete',
  [CalendarMenuActionIds.duplicate]: 'Duplicate',
  [CalendarMenuActionIds.openInNewTab]: 'Open in New Tab',
  [CalendarMenuActionIds.rename]: 'Rename',
  [CalendarMenuActionIds.unschedule]: 'Unschedule',
}

const action = (id: CalendarMenuActionId): CalendarMenuOption => ({
  id,
  label: LABELS[id],
  type: 'action',
})

const destructive = (id: CalendarMenuActionId): CalendarMenuOption => ({
  id,
  isDestructive: true,
  label: LABELS[id],
  type: 'action',
})

/**
 * Puts one rule between each band of actions. A band nobody can use is not
 * drawn, so the rules are laid down after the bands are known rather than
 * written into them — otherwise hiding an action leaves its rule behind.
 */
const withSeparators = (
  bands: readonly (readonly CalendarMenuOption[])[]
): CalendarMenuOption[] =>
  bands
    .filter((band) => band.length > 0)
    .flatMap((band, index) =>
      index === 0 ? [...band] : [{ type: 'separator' as const }, ...band]
    )

/**
 * What a right-click on this item offers.
 *
 * Pure, and told everything it needs: the item says what it is and whether it
 * has a page of its own, and the capabilities say what the surface around the
 * calendar can open. Nothing here knows an endpoint or a feature flag.
 */
export const buildCalendarMenuOptions = (
  item: CalendarItem,
  {
    canAddToBoard = false,
    canAddToPlan = false,
    canDuplicateMoment = false,
    canEdit = true,
  }: CalendarMenuCapabilities = {}
): CalendarMenuOption[] => {
  // A link is only offered when there is somewhere to send a person.
  const link =
    item.href === undefined
      ? []
      : [
          action(CalendarMenuActionIds.openInNewTab),
          action(CalendarMenuActionIds.copyLink),
        ]

  // A reader is left the ways to read the item and nothing else, whatever the
  // host around the calendar could otherwise answer.
  if (!canEdit) return withSeparators([link])

  if (item.kind === 'content') {
    // Everything that changes a piece of content goes through `/content/v2`
    // under its legacy id, because v3 serves no writable name and no delete.
    // Without one there is nothing to offer but the way to the page itself.
    const canWrite = item.legacyId !== undefined

    return withSeparators([
      [
        ...link,
        ...(canWrite
          ? [
              action(CalendarMenuActionIds.rename),
              action(CalendarMenuActionIds.duplicate),
            ]
          : []),
      ],
      canWrite ? [destructive(CalendarMenuActionIds.delete)] : [],
    ])
  }

  // Stories and blocks reach the grid but are not drawn, and nothing here knows
  // what acting on one would mean.
  if (item.kind !== 'moment') return []

  return withSeparators([
    [
      ...link,
      action(CalendarMenuActionIds.rename),
      ...(canDuplicateMoment ? [action(CalendarMenuActionIds.duplicate)] : []),
    ],
    [
      ...(canAddToPlan ? [action(CalendarMenuActionIds.addToPlan)] : []),
      ...(canAddToBoard
        ? [
            action(CalendarMenuActionIds.addToBoard),
            action(CalendarMenuActionIds.unschedule),
          ]
        : []),
    ],
    [destructive(CalendarMenuActionIds.delete)],
  ])
}
